import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useLocationStore } from '@/store/location';
import {
  getPOSSource,
  type POSImportData,
  type POSParsedProduct,
  type OutletLocationMapping,
} from '@/lib/import/sources';
import type { ImportProgressState } from '@/components/import/ImportProgress';
import type { ImportResultsData } from '@/components/import/ImportResults';

export type POSImportStep =
  | 'upload'
  | 'outlet-mapping'
  | 'preview'
  | 'importing'
  | 'results';

export interface UsePOSImportReturn {
  // State
  step: POSImportStep;
  sourceId: string | null;
  isLoading: boolean;
  error: string | null;
  detectedOutlets: string[];
  outletMapping: OutletLocationMapping;
  parsedData: POSImportData | null;
  progress: ImportProgressState | null;
  results: ImportResultsData | null;

  // Actions
  setSourceId: (id: string) => void;
  uploadFile: (file: File) => Promise<void>;
  setOutletMapping: (mapping: OutletLocationMapping) => void;
  confirmMapping: () => void;
  startImport: () => Promise<void>;
  goBack: () => void;
  reset: () => void;
}

const BATCH_SIZE = 50;

export function usePOSImport(): UsePOSImportReturn {
  const queryClient = useQueryClient();
  const { currentLocation, locations } = useLocationStore();

  const [step, setStep] = useState<POSImportStep>('upload');
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [detectedOutlets, setDetectedOutlets] = useState<string[]>([]);
  const [outletMapping, setOutletMapping] = useState<OutletLocationMapping>(new Map());
  const [parsedData, setParsedData] = useState<POSImportData | null>(null);
  const [progress, setProgress] = useState<ImportProgressState | null>(null);
  const [results, setResults] = useState<ImportResultsData | null>(null);

  // Step 1: Upload file and detect outlets
  const uploadFile = useCallback(async (uploadedFile: File) => {
    if (!sourceId) {
      setError('No import source selected');
      return;
    }

    const source = getPOSSource(sourceId);
    if (!source) {
      setError(`Unknown import source: ${sourceId}`);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // Detect outlets from the file headers
      const outlets = await source.detectOutlets(uploadedFile);
      setFile(uploadedFile);
      setDetectedOutlets(outlets);

      // Auto-map outlets if only one BlazePOS location exists
      if (locations.length === 1 && outlets.length > 0) {
        const autoMap = new Map<string, string>();
        // Map all outlets to the single location by default
        outlets.forEach(o => autoMap.set(o, locations[0].id));
        setOutletMapping(autoMap);
      }

      if (outlets.length > 0) {
        setStep('outlet-mapping');
      } else {
        // No outlets detected -- skip mapping step, parse directly
        const data = await source.parse(uploadedFile);
        setParsedData(data);
        setStep('preview');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse file');
    } finally {
      setIsLoading(false);
    }
  }, [sourceId, locations]);

  // Step 2 -> 3: Confirm outlet mapping and parse the full file
  const confirmMapping = useCallback(async () => {
    if (!sourceId || !file) return;

    const source = getPOSSource(sourceId);
    if (!source) return;

    setIsLoading(true);
    setError(null);

    try {
      const data = await source.parse(file, outletMapping);
      setParsedData(data);
      setStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse file');
    } finally {
      setIsLoading(false);
    }
  }, [sourceId, file, outletMapping]);

  // Step 3 -> 4: Execute the import
  const startImport = useCallback(async () => {
    if (!parsedData || !currentLocation) return;

    setStep('importing');
    setError(null);

    const importResults: ImportResultsData = {
      products: { created: 0, updated: 0, failed: 0, errors: [] },
      rawMaterials: { created: 0, updated: 0, failed: 0, errors: [] },
      modifiers: { groupsCreated: 0, optionsCreated: 0, failed: 0, errors: [] },
      categories: { created: 0, updated: 0, failed: 0, errors: [] },
    };

    try {
      // Phase 1: Create categories
      const categoryMap = new Map<string, string>();

      // Load existing categories
      const { data: existingCategories } = await supabase
        .from('categories')
        .select('id, name')
        .eq('location_id', currentLocation.id)
        .eq('is_active', true);

      existingCategories?.forEach(c => {
        categoryMap.set(c.name.toLowerCase().trim(), c.id);
      });

      // Create missing categories
      const newCategories = parsedData.categories.filter(
        catName => !categoryMap.has(catName.toLowerCase().trim())
      );

      if (newCategories.length > 0) {
        setProgress({
          phase: 'categories',
          current: 0,
          total: newCategories.length,
          message: 'Creating categories...',
        });

        for (let i = 0; i < newCategories.length; i++) {
          try {
            const { data, error: catError } = await supabase
              .from('categories')
              .insert({
                location_id: currentLocation.id,
                name: newCategories[i],
                is_active: true,
                sort_order: 0,
              })
              .select('id')
              .single();

            if (catError) throw catError;
            if (data) {
              categoryMap.set(newCategories[i].toLowerCase().trim(), data.id);
              importResults.categories.created++;
            }
          } catch (err) {
            importResults.categories.failed++;
            importResults.categories.errors.push({
              row: 0,
              message: `Failed to create category "${newCategories[i]}": ${err instanceof Error ? err.message : 'Unknown error'}`,
            });
          }

          setProgress({
            phase: 'categories',
            current: i + 1,
            total: newCategories.length,
            message: `Creating category ${i + 1} of ${newCategories.length}`,
          });
        }
      }

      // Phase 2: Import products
      const products = parsedData.products;
      const totalProducts = products.length;

      // Load existing products for SKU matching
      const { data: existingProducts } = await supabase
        .from('products')
        .select('id, name, sku')
        .eq('organization_id', currentLocation.organization_id)
        .eq('is_active', true);

      const existingProductMap = new Map<string, { id: string; name: string }>();
      existingProducts?.forEach(p => {
        if (p.sku) {
          existingProductMap.set(p.sku.toLowerCase().trim(), { id: p.id, name: p.name });
        }
      });

      // Load existing suppliers for name matching
      const { data: existingSuppliers } = await supabase
        .from('suppliers')
        .select('id, name')
        .eq('organization_id', currentLocation.organization_id)
        .eq('is_active', true);

      const supplierMap = new Map<string, string>();
      existingSuppliers?.forEach(s => {
        supplierMap.set(s.name.toLowerCase().trim(), s.id);
      });

      for (let i = 0; i < totalProducts; i += BATCH_SIZE) {
        const batch = products.slice(i, i + BATCH_SIZE);

        setProgress({
          phase: 'products',
          current: i + batch.length,
          total: totalProducts,
          message: `Importing products ${i + 1} to ${Math.min(i + batch.length, totalProducts)}`,
        });

        for (const product of batch) {
          try {
            await importSingleProduct(
              product,
              currentLocation,
              categoryMap,
              existingProductMap,
              supplierMap,
              outletMapping,
              importResults
            );
          } catch (err) {
            importResults.products.failed++;
            importResults.products.errors.push({
              row: product.sourceRows[0],
              message: `${product.name}: ${err instanceof Error ? err.message : 'Unknown error'}`,
            });
          }
        }
      }

      setResults(importResults);
      setStep('results');

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['variant-attributes'] });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Import failed');
      setResults(importResults);
      setStep('results');
    }
  }, [parsedData, currentLocation, outletMapping, queryClient]);

  const goBack = useCallback(() => {
    if (step === 'preview') {
      if (detectedOutlets.length > 0) {
        setStep('outlet-mapping');
      } else {
        setStep('upload');
      }
    } else if (step === 'outlet-mapping') {
      setStep('upload');
    }
  }, [step, detectedOutlets]);

  const reset = useCallback(() => {
    setStep('upload');
    setIsLoading(false);
    setError(null);
    setFile(null);
    setDetectedOutlets([]);
    setOutletMapping(new Map());
    setParsedData(null);
    setProgress(null);
    setResults(null);
  }, []);

  return {
    step,
    sourceId,
    isLoading,
    error,
    detectedOutlets,
    outletMapping,
    parsedData,
    progress,
    results,
    setSourceId,
    uploadFile,
    setOutletMapping,
    confirmMapping,
    startImport,
    goBack,
    reset,
  };
}

// ---------------------------------------------------------------------------
// Database import helpers
// ---------------------------------------------------------------------------

interface LocationRef {
  id: string;
  organization_id: string;
}

async function importSingleProduct(
  product: POSParsedProduct,
  location: LocationRef,
  categoryMap: Map<string, string>,
  existingProductMap: Map<string, { id: string; name: string }>,
  supplierMap: Map<string, string>,
  outletMapping: OutletLocationMapping,
  results: ImportResultsData
): Promise<void> {
  // Resolve category
  let categoryId: string | null = null;
  if (product.category) {
    categoryId = categoryMap.get(product.category.toLowerCase().trim()) || null;
  }

  // Resolve supplier
  let supplierId: string | null = null;
  if (product.supplier_name) {
    supplierId = supplierMap.get(product.supplier_name.toLowerCase().trim()) || null;
  }

  // Check for existing product by SKU
  const existingProduct = product.sku
    ? existingProductMap.get(product.sku.toLowerCase().trim())
    : undefined;

  const productData = {
    name: product.name,
    sku: product.sku,
    description: product.description || null,
    price: product.price,
    cost: product.cost ?? null,
    category_id: categoryId,
    supplier_id: supplierId,
    is_active: true,
    is_available: true,
    is_sellable: product.is_sellable,
    track_inventory: product.track_inventory,
    product_type: product.product_type,
    updated_at: new Date().toISOString(),
  };

  let productId: string;

  if (existingProduct) {
    // Update existing product
    const { error } = await supabase
      .from('products')
      .update(productData)
      .eq('id', existingProduct.id);

    if (error) throw error;
    productId = existingProduct.id;
    results.products.updated++;
  } else {
    // Create new product
    const { data, error } = await supabase
      .from('products')
      .insert({
        ...productData,
        location_id: location.id,
        organization_id: location.organization_id,
      })
      .select('id')
      .single();

    if (error) throw error;
    productId = data.id;
    results.products.created++;

    // Add to the existing map so duplicate SKUs in the same batch are caught
    if (product.sku) {
      existingProductMap.set(product.sku.toLowerCase().trim(), {
        id: productId,
        name: product.name,
      });
    }
  }

  // Create inventory records for the base product (per-outlet)
  if (product.track_inventory && product.outletInventory) {
    await importOutletInventory(
      productId,
      null,
      product.outletInventory,
      outletMapping
    );
  }

  // Handle variants
  if (product.variants && product.variants.length > 0) {
    await importProductVariants(
      productId,
      location,
      product.variants,
      outletMapping,
      product.track_inventory
    );
  }

  // Handle composite components
  if (product.compositeComponents && product.compositeComponents.length > 0) {
    await importCompositeComponents(
      productId,
      product.compositeComponents,
      existingProductMap,
      location
    );
  }
}

/**
 * Import product variants, creating variant attributes and values as needed.
 */
async function importProductVariants(
  productId: string,
  location: LocationRef,
  variants: NonNullable<POSParsedProduct['variants']>,
  outletMapping: OutletLocationMapping,
  trackInventory: boolean
): Promise<void> {
  // Collect all attribute names used across variants
  const attrNames = new Set<string>();
  for (const v of variants) {
    for (const name of Object.keys(v.attributes)) {
      attrNames.add(name);
    }
  }

  // Ensure variant_attributes exist at org level
  const attrIdMap = new Map<string, string>(); // attribute name -> attribute id

  for (const attrName of attrNames) {
    // Check if attribute exists
    const { data: existingAttr } = await supabase
      .from('variant_attributes')
      .select('id')
      .eq('organization_id', location.organization_id)
      .ilike('name', attrName)
      .maybeSingle();

    if (existingAttr) {
      attrIdMap.set(attrName, existingAttr.id);
    } else {
      const { data: newAttr } = await supabase
        .from('variant_attributes')
        .insert({
          organization_id: location.organization_id,
          name: attrName,
        })
        .select('id')
        .single();

      if (newAttr) {
        attrIdMap.set(attrName, newAttr.id);
      }
    }
  }

  // Link product to variant attributes
  for (const [attrName, attrId] of attrIdMap) {
    await supabase
      .from('product_variant_attributes')
      .upsert(
        { product_id: productId, attribute_id: attrId },
        { onConflict: 'product_id,attribute_id' }
      );

    // Ensure all attribute values exist
    const valuesForAttr = new Set<string>();
    for (const v of variants) {
      if (v.attributes[attrName]) {
        valuesForAttr.add(v.attributes[attrName]);
      }
    }

    for (const val of valuesForAttr) {
      const { data: existingVal } = await supabase
        .from('variant_attribute_values')
        .select('id')
        .eq('attribute_id', attrId)
        .ilike('value', val)
        .maybeSingle();

      if (!existingVal) {
        await supabase
          .from('variant_attribute_values')
          .insert({
            attribute_id: attrId,
            value: val,
          });
      }
    }
  }

  // Create product_variants
  for (const variant of variants) {
    const { data: existingVariant } = variant.sku
      ? await supabase
          .from('product_variants')
          .select('id')
          .eq('product_id', productId)
          .eq('sku', variant.sku)
          .maybeSingle()
      : { data: null };

    let variantId: string;

    if (existingVariant) {
      // Update existing variant
      await supabase
        .from('product_variants')
        .update({
          name: variant.name,
          price: variant.price,
          cost: variant.cost ?? null,
          is_active: true,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existingVariant.id);

      variantId = existingVariant.id;
    } else {
      const { data: newVariant, error } = await supabase
        .from('product_variants')
        .insert({
          product_id: productId,
          name: variant.name,
          sku: variant.sku || null,
          price: variant.price,
          cost: variant.cost ?? null,
          is_active: true,
          sort_order: 0,
        })
        .select('id')
        .single();

      if (error) throw error;
      variantId = newVariant.id;
    }

    // Link variant to attribute values
    for (const [attrName, attrValue] of Object.entries(variant.attributes)) {
      const attrId = attrIdMap.get(attrName);
      if (!attrId) continue;

      const { data: attrValRecord } = await supabase
        .from('variant_attribute_values')
        .select('id')
        .eq('attribute_id', attrId)
        .ilike('value', attrValue)
        .maybeSingle();

      if (attrValRecord) {
        await supabase
          .from('product_variant_attribute_values')
          .upsert(
            {
              variant_id: variantId,
              attribute_id: attrId,
              attribute_value_id: attrValRecord.id,
            },
            { onConflict: 'variant_id,attribute_id' }
          );
      }
    }

    // Create variant inventory records
    if (trackInventory && variant.outletInventory) {
      await importOutletInventory(null, variantId, variant.outletInventory, outletMapping);
    }
  }
}

/**
 * Import composite product components (BOM), matching component products by SKU.
 */
async function importCompositeComponents(
  parentProductId: string,
  components: NonNullable<POSParsedProduct['compositeComponents']>,
  existingProductMap: Map<string, { id: string; name: string }>,
  location: LocationRef
): Promise<void> {
  // Clear existing components for this parent to avoid duplicates on re-import
  await supabase
    .from('composite_product_components')
    .delete()
    .eq('parent_product_id', parentProductId);

  for (const component of components) {
    // Try to find the component product by SKU
    let componentProductId: string | null = null;

    if (component.sku) {
      const existing = existingProductMap.get(component.sku.toLowerCase().trim());
      if (existing) {
        componentProductId = existing.id;
      }
    }

    // If not found by SKU, try by name
    if (!componentProductId) {
      const { data } = await supabase
        .from('products')
        .select('id')
        .eq('organization_id', location.organization_id)
        .ilike('name', component.name)
        .eq('is_active', true)
        .maybeSingle();

      if (data) {
        componentProductId = data.id;
      }
    }

    if (componentProductId) {
      await supabase
        .from('composite_product_components')
        .insert({
          parent_product_id: parentProductId,
          component_product_id: componentProductId,
          quantity: component.quantity,
        });
    }
    // If the component product was not found, we skip it silently.
    // The product will need to be imported first or linked manually.
  }
}

/**
 * Create or update inventory records for mapped outlets.
 * Either productId or variantId should be provided, not both.
 */
async function importOutletInventory(
  productId: string | null,
  variantId: string | null,
  outletInventory: Record<string, { quantity: number; reorder_point?: number; restock_level?: number }>,
  outletMapping: OutletLocationMapping
): Promise<void> {
  for (const [outletName, inv] of Object.entries(outletInventory)) {
    const locationId = outletMapping.get(outletName);
    if (!locationId) continue; // Outlet not mapped -- skip

    if (productId) {
      const { data: existingInventory } = await supabase
        .from('inventory_items')
        .select('id, quantity_on_hand')
        .eq('product_id', productId)
        .eq('location_id', locationId)
        .maybeSingle();

      // Product-level inventory
      const { data: upsertedInventory, error: upsertError } = await supabase
        .from('inventory_items')
        .upsert(
          {
            product_id: productId,
            location_id: locationId,
            quantity_on_hand: inv.quantity,
            quantity_foh: inv.quantity,
            quantity_boh: 0,
            quantity_reserved: 0,
            reorder_point: inv.reorder_point ?? null,
            reorder_quantity: inv.restock_level ?? null,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'product_id,location_id' }
        )
        .select('id')
        .single();

      if (upsertError) throw upsertError;

      const quantityBefore = existingInventory?.quantity_on_hand ?? 0;
      const quantityAfter = inv.quantity;
      const quantityChange = quantityAfter - quantityBefore;
      if (upsertedInventory?.id && quantityChange !== 0) {
        await supabase
          .from('inventory_transactions')
          .insert({
            inventory_item_id: upsertedInventory.id,
            transaction_type: 'adjusted',
            quantity_change: quantityChange,
            quantity_before: quantityBefore,
            quantity_after: quantityAfter,
            reference_type: 'import',
            notes: `POS import stock sync (${outletName})`,
          });
      }
    } else if (variantId) {
      const { data: existingVariantInventory } = await supabase
        .from('variant_inventory_items')
        .select('id, quantity_on_hand')
        .eq('variant_id', variantId)
        .eq('location_id', locationId)
        .maybeSingle();

      // Variant-level inventory
      const { data: upsertedVariantInventory, error: upsertVariantError } = await supabase
        .from('variant_inventory_items')
        .upsert(
          {
            variant_id: variantId,
            location_id: locationId,
            quantity_on_hand: inv.quantity,
            quantity_reserved: 0,
            reorder_point: inv.reorder_point ?? null,
            reorder_quantity: inv.restock_level ?? null,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'variant_id,location_id' }
        )
        .select('id')
        .single();

      if (upsertVariantError) throw upsertVariantError;

      const quantityBefore = existingVariantInventory?.quantity_on_hand ?? 0;
      const quantityAfter = inv.quantity;
      const quantityChange = quantityAfter - quantityBefore;
      if (upsertedVariantInventory?.id && quantityChange !== 0) {
        await supabase
          .from('variant_inventory_transactions')
          .insert({
            variant_inventory_item_id: upsertedVariantInventory.id,
            transaction_type: 'adjusted',
            quantity_change: quantityChange,
            quantity_before: quantityBefore,
            quantity_after: quantityAfter,
            reference_type: 'import',
            notes: `POS import variant stock sync (${outletName})`,
          });
      }
    }
  }
}
